import React from "react";
import { Button, Card, Input, Label } from "reactstrap";
import { Link } from "react-router-dom";

const initials = (name?: string) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/** What the merge does to this side, set beside its identity. */
const RoleChip = ({ side }: { side: "main" | "merge" }) =>
  side === "main" ? (
    <span className="compare-role compare-role--main">
      <i className="ri-shield-check-line"></i>Kept
    </span>
  ) : (
    <span className="compare-role compare-role--merge">
      <i className="ri-delete-bin-6-line"></i>Deleted after merge
    </span>
  );

const Meta = ({ record }: { record: any }) => (
  <span className="compare-header__meta">
    {record?.organizationType?.name || "No organization type"} · ID{" "}
    {record.id} ·{" "}
    <Link to={`/contractors/${record.id}`} target="_blank" rel="noreferrer">
      Open profile<i className="ri-external-link-line ms-1"></i>
      <span className="visually-hidden"> (opens in a new tab)</span>
    </Link>
  </span>
);

const IdentitySkeleton = () => (
  <div className="d-flex align-items-center gap-2 placeholder-glow">
    <span className="placeholder rounded-circle compare-header__avatar"></span>
    <span className="placeholder col-7"></span>
  </div>
);

interface CompareHeaderProps {
  main: { record: any; loading: boolean };
  merge: { record: any; loading: boolean };
  picker: React.ReactNode;
  onlyDifferences: boolean;
  onToggleDifferences: (value: boolean) => void;
  differences: { differing: number; total: number } | null;
  onSwap: (() => void) | null;
}

/**
 * Column headers for the comparison. Sticky on wide screens, so it stays
 * obvious which column is which however far the user scrolls, and the
 * contractor to merge can be changed or swapped without scrolling back up.
 */
const CompareHeader = ({
  main,
  merge,
  picker,
  onlyDifferences,
  onToggleDifferences,
  differences,
  onSwap,
}: CompareHeaderProps) => (
  <Card className="compare-header mb-3">
    <div className="compare-grid">
      <div className="compare-header__corner">
        <span className="compare-header__count">
          {differences ? (
            <>
              <strong>{differences.differing}</strong> of {differences.total}{" "}
              fields differ
            </>
          ) : (
            "Side-by-side review"
          )}
        </span>
        <div className="form-check form-switch mb-0">
          <Input
            type="switch"
            role="switch"
            className="form-check-input"
            id="compare-only-differences"
            checked={onlyDifferences}
            disabled={!differences}
            onChange={(e) => onToggleDifferences(e.target.checked)}
          />
          <Label
            className="form-check-label fs-13"
            for="compare-only-differences"
          >
            Only differences
          </Label>
        </div>
        {onSwap ? (
          <Button
            color="link"
            size="sm"
            className="compare-header__swap"
            onClick={onSwap}
            title="Keep the right-hand contractor instead"
          >
            <i className="ri-arrow-left-right-line"></i>
            Swap sides
          </Button>
        ) : null}
      </div>

      <div className="compare-header__column compare-header__column--main">
        {main.loading || !main.record ? (
          <IdentitySkeleton />
        ) : (
          <>
            <div className="d-flex align-items-center gap-2 merge-shrink">
              <div className="avatar-title rounded-circle bg-primary-subtle text-primary fw-semibold fs-13 flex-shrink-0 compare-header__avatar">
                {initials(main.record.contractorName)}
              </div>
              <h2
                className="compare-header__name text-truncate"
                title={main.record.contractorName}
              >
                {main.record.contractorName}
              </h2>
            </div>
            <div className="compare-header__meta-row">
              <Meta record={main.record} />
              <RoleChip side="main" />
            </div>
          </>
        )}
      </div>

      <div className="compare-header__column compare-header__column--merge">
        {picker}
        <div className="compare-header__meta-row">
          {merge.record ? (
            <Meta record={merge.record} />
          ) : (
            <span className="compare-header__meta">
              Choose the duplicate to compare
            </span>
          )}
          <RoleChip side="merge" />
        </div>
      </div>
    </div>
  </Card>
);

export default CompareHeader;
